export type DeviceStatus =
  | "discovered_unclaimed"
  | "claimed_active"
  | "claimed_inactive";

/**
 * Lacunas curtas de telemetria nao devem virar "sem sinal" operacional.
 * Em campo, perdas de poucos segundos podem ser jitter de rede/broker/worker.
 */
export const TELEMETRY_GAP_GRACE_MS = 15_000;


export interface Device {
  id: string;
  /**
   * Identidade de REDE. Imutavel: a ACL do broker prende o topico ao usuario,
   * e usuario e `mqttId` sao o mesmo valor. Renomear quebraria a publicacao
   * da placa em silencio — em QoS 0 nao ha confirmacao.
   */
  mqttId: string;
  /**
   * Identidade OPERACIONAL: o nome que quem opera a fabrica usa ("Extrusora
   * 2"). Cosmetico — nao participa de ACL, topico nem chave.
   *
   * `null` significa que ninguem nomeou ainda, e e diferente de string vazia.
   * A interface cai no `mqttId` nesse caso, em vez de inventar um nome que
   * pareceria escolhido.
   */
  nomeFantasia?: string | null;
  serialNumber?: string | null;
  mac?: string | null;
  status: DeviceStatus;
  currentWorkspaceId?: string | null;
  firstSeenAt?: string;
  lastSeenAt?: string;
}

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  createdAt: string;
}

export interface DeviceClaim {
  id: string;
  deviceId: string;
  workspaceId: string;
  claimedByUserId: string;
  claimedAt: string;
}

export interface DeviceMetaSnapshot {
  id: string;
  deviceId: string;
  configHashHex: string;
  firmwareVersion: string;
  configSchema: number;
  metaJson: string;
  receivedAt: string;
  isCurrent: boolean;
}

export type PortSnapshotSource = "telemetry" | "sync" | "legacy";

export function getPortSnapshotSourcePriority(
  source: PortSnapshotSource | undefined,
): number {
  if (source === "telemetry") return 2;
  if (source === "sync") return 1;
  return 0;
}

export interface DevicePortSnapshot {
  deviceId: string;
  mqttId: string;
  portIndex: number;
  mode: string;
  v?: number;
  acc?: number;
  s: number;
  measuredAt: string;
  configHashHex: string;
  /** Origem MQTT usada no desempate canônico. Ausente em dados legados. */
  source?: PortSnapshotSource;
  /** Momento em que o worker recebeu a publicação. */
  receivedAt?: string;
}

export interface DeviceDigitalInputSnapshot {
  deviceId: string;
  mqttId: string;
  portIndex: number;
  address: string;
  gpio: number;
  level: number;
  active: number;
  polarity?: string;
  measuredAt: string;
  configHashHex: string;
}

export interface DeviceSyncSnapshot {
  deviceId: string;
  mqttId: string;
  deviceUptimeSec?: number;
  uptimeMs?: number;
  ip?: string;
  rssi?: number;
  firmwareVersion?: string;
  measuredAt: string;
  configHashHex: string;
}

// ---------------------------------------------------------------------------
// Identidade e autorizacao
// ---------------------------------------------------------------------------
// `WorkspaceRole`, `User` e `WorkspaceMembership` viviam aqui como tipos
// MORTOS: declarados, exportados e importados por ninguem. Eram o esboco de um
// modelo com papel por WORKSPACE e quatro niveis (owner/admin/operator/viewer).
//
// O modelo que passou a valer prende o papel na ORGANIZACAO, e com dois niveis
// — decisao do dono do produto, que fundiu dono e engenheiro num `admin` so.
// Prender na organizacao e o que permite a mesma pessoa ser `admin` na propria
// planta e `operador` na do cliente, caso que o canal de integradores torna
// provavel.
//
// Os antigos sairam em vez de conviver: dois modelos de papel no mesmo pacote
// e um convite a importar o errado.
export * from "./auth/authorization";
export * from "./workspace";
export * from "./navigation";

// `auth/password` NAO entra no barril, de proposito.
//
// O front importa `@ghm/domain` para tipos e regras puras. O barril e um unico
// grafo: re-exportar o modulo de senha arrastava `node:crypto` e `node:util`
// para dentro do bundle do navegador, e o `vite build` quebrava com
// "promisify is not exported by __vite-browser-external". Nao era um detalhe de
// empacotamento — era o codigo de hash de senha sendo servido ao cliente.
//
// Quem precisa dele e servidor, e importa pelo caminho explicito:
//     import { hashPassword } from "@ghm/domain/senha";
//
// O import mais feio e a defesa: nao da para puxar isso para o navegador por
// distracao.
